/**
 * Payment sources ("Betalt med"): thin Dexie wrappers around the pure rules.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import type { Company, PaymentSource, SavedColumnMapping, Uuid } from '../types';
import { defaultPaymentSources, validatePaymentSource, type PaymentSourceInput, type PaymentSourceRuleError } from './rules';

export class PaymentSourceRuleViolation extends Error {
  constructor(public readonly errors: PaymentSourceRuleError[]) {
    super(errors.map((e) => e.message).join('; '));
    this.name = 'PaymentSourceRuleViolation';
  }
}

/**
 * Create the three default sources for a company. Idempotent: a company that
 * already has sources gets its existing list back. Also sets the company's
 * default payment source to the bank source when none is set yet.
 */
export async function seedDefaultPaymentSources(company: Company, db: BookkeepingDB = defaultDb): Promise<PaymentSource[]> {
  return db.transaction('rw', db.paymentSources, db.companySettings, async () => {
    const existing = await db.paymentSources.where('companyId').equals(company.id).toArray();
    const sources = existing.length ? existing : defaultPaymentSources(company);
    if (!existing.length) await db.paymentSources.bulkAdd(sources);
    const settings = await db.companySettings.get(company.id);
    if (settings && settings.defaultPaymentSourceId === null) {
      const bank = sources.find((s) => s.kind === 'bank' && s.active);
      if (bank) await db.companySettings.put({ ...settings, defaultPaymentSourceId: bank.id, updatedAt: new Date().toISOString() });
    }
    return sources;
  });
}

export async function listPaymentSources(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<PaymentSource[]> {
  const list = await db.paymentSources.where('companyId').equals(companyId).toArray();
  return list.sort((a, b) => Number(b.active) - Number(a.active) || a.createdAt.localeCompare(b.createdAt) || a.name.localeCompare(b.name, 'sv'));
}

export async function paymentSourceMap(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<Map<Uuid, PaymentSource>> {
  return new Map((await listPaymentSources(companyId, db)).map((s) => [s.id, s]));
}

export async function addPaymentSource(
  input: PaymentSourceInput & { companyId: Uuid; bankProfileId?: string },
  db: BookkeepingDB = defaultDb,
): Promise<PaymentSource> {
  return db.transaction('rw', db.paymentSources, async () => {
    const existing = await db.paymentSources.where('companyId').equals(input.companyId).toArray();
    const names = new Set(existing.filter((s) => s.active).map((s) => s.name.toLowerCase()));
    const result = validatePaymentSource(input, names);
    if (!result.ok) throw new PaymentSourceRuleViolation(result.errors);
    if (input.linkedBankSourceId && !existing.some((s) => s.id === input.linkedBankSourceId && s.kind === 'bank')) {
      throw new PaymentSourceRuleViolation([{ code: 'missing_linked_bank', message: 'Det kopplade bankkontot finns inte' }]);
    }
    const source: PaymentSource = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      name: input.name.trim(),
      kind: input.kind,
      accountNumber: input.accountNumber,
      active: true,
      createdAt: new Date().toISOString(),
      ...(input.linkedBankSourceId ? { linkedBankSourceId: input.linkedBankSourceId } : {}),
      ...(input.bankProfileId ? { bankProfileId: input.bankProfileId } : {}),
    };
    await db.paymentSources.add(source);
    return source;
  });
}

export async function renamePaymentSource(id: Uuid, name: string, db: BookkeepingDB = defaultDb): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new PaymentSourceRuleViolation([{ code: 'missing_name', message: 'Betalkällan behöver ett namn' }]);
  await db.paymentSources.update(id, { name: trimmed, updatedAt: new Date().toISOString() });
}

/** Remember a confirmed CSV column mapping (and bank profile) on the source. */
export async function saveImportMapping(
  id: Uuid,
  mapping: SavedColumnMapping,
  bankProfileId?: string,
  db: BookkeepingDB = defaultDb,
): Promise<void> {
  await db.paymentSources.update(id, { importMapping: mapping, ...(bankProfileId ? { bankProfileId } : {}), updatedAt: new Date().toISOString() });
}

/** Sources are never deleted (verifikationer reference them); they are hidden. */
export async function setPaymentSourceActive(id: Uuid, active: boolean, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.transaction('rw', db.paymentSources, db.companySettings, async () => {
    const source = await db.paymentSources.get(id);
    if (!source) throw new Error('Payment source not found');
    await db.paymentSources.update(id, { active, updatedAt: new Date().toISOString() });
    if (!active) {
      const settings = await db.companySettings.get(source.companyId);
      if (settings?.defaultPaymentSourceId === id) {
        const fallback = (await db.paymentSources.where('companyId').equals(source.companyId).toArray()).find((s) => s.active && s.id !== id && s.kind === 'bank');
        await db.companySettings.put({ ...settings, defaultPaymentSourceId: fallback?.id ?? null, updatedAt: new Date().toISOString() });
      }
    }
  });
}
